import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WholesaleDiscountsPage.css';

export default function WholesaleDiscountsPage() {
  const navigate = useNavigate();

  const [defaultDisc,    setDefaultDisc]    = useState('');
  const [accounts,       setAccounts]       = useState([]);   // [{customer_id,label,discount}]
  const [whslCustomers,  setWhslCustomers]  = useState([]);   // for the account picker
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState(null);

  const [newCustomerId, setNewCustomerId] = useState('');
  const [newLabel,      setNewLabel]      = useState('');
  const [newDiscount,   setNewDiscount]   = useState('');

  function reload() {
    setLoading(true);
    Promise.all([
      axios.get('/api/wholesale/config/discounts'),
      axios.get('/api/wholesale/whsl-customers'),
    ]).then(([cfgRes, custRes]) => {
      setDefaultDisc(cfgRes.data.default_discount || '');
      setAccounts(cfgRes.data.accounts || []);
      setWhslCustomers(custRes.data || []);
      setLoading(false);
    }).catch(err => {
      const status = err.response?.status;
      const detail = err.response?.data?.error;
      setError(
        status === 401
          ? 'Failed to load discount config — you may have been signed out. Try refreshing the page.'
          : `Failed to load discount config${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ''}`
      );
      setLoading(false);
    });
  }

  useEffect(() => { reload(); }, []);

  const customerById = {};
  whslCustomers.forEach(c => { customerById[String(c.id)] = c; });

  function saveDefault() {
    if (!defaultDisc) return;
    setSaving(true);
    axios.put('/api/wholesale/config/discounts/default', { discount: defaultDisc })
      .then(r => { setAccounts(r.data.accounts || []); setSaving(false); })
      .catch(() => { setError('Failed to save default discount'); setSaving(false); });
  }

  function upsertAccount(e) {
    e.preventDefault();
    if (!newCustomerId.trim() || !newDiscount) return;
    setSaving(true);
    axios.post('/api/wholesale/config/discounts/accounts', {
      customer_id: newCustomerId.trim(), label: newLabel.trim(), discount: newDiscount,
    }).then(r => {
      setAccounts(r.data.accounts || []);
      setNewCustomerId(''); setNewLabel(''); setNewDiscount('');
      setSaving(false);
    }).catch(() => { setError('Failed to save account'); setSaving(false); });
  }

  function editAccountDiscount(acct, discount) {
    setSaving(true);
    axios.post('/api/wholesale/config/discounts/accounts', {
      customer_id: acct.customer_id, label: acct.label, discount,
    }).then(r => { setAccounts(r.data.accounts || []); setSaving(false); })
      .catch(() => { setError('Failed to update discount'); setSaving(false); });
  }

  function removeAccount(customerId) {
    setSaving(true);
    axios.delete(`/api/wholesale/config/discounts/accounts/${encodeURIComponent(customerId)}`)
      .then(r => { setAccounts(r.data.accounts || []); setSaving(false); })
      .catch(() => { setError('Failed to remove account'); setSaving(false); });
  }

  return (
    <div className="whdisc-page">
      <div className="whdisc-header">
        <button className="whdisc-back" onClick={() => navigate('/wholesale/applications')}>
          ← Back
        </button>
        <div>
          <h1>Wholesale Discounts</h1>
          <p className="whdisc-muted">
            Set the global wholesale discount rate, with optional per-account overrides keyed by
            Shopify customer ID. Applied automatically whenever a WHSL account submits an order.
          </p>
        </div>
      </div>

      {error && (
        <div className="whdisc-error">
          {error} <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <p className="whdisc-muted">Loading…</p>
      ) : (
        <>
          <div className="whdisc-section">
            <label className="whdisc-label">Default discount</label>
            <p className="whdisc-muted-sm">Applied to any WHSL account without a custom rate below.</p>
            <div className="whdisc-row">
              <div className="whdisc-pct-wrap">
                <input
                  className="whdisc-input whdisc-input-pct"
                  type="number" step="0.01" min="0" max="100"
                  value={defaultDisc}
                  onChange={e => setDefaultDisc(e.target.value)}
                  disabled={saving}
                />
                <span className="whdisc-pct-sign">%</span>
              </div>
              <button className="whdisc-btn-primary" onClick={saveDefault} disabled={saving || !defaultDisc}>
                Save
              </button>
            </div>
          </div>

          <div className="whdisc-section">
            <label className="whdisc-label">Add / update an account rate</label>
            <form className="whdisc-add-form" onSubmit={upsertAccount}>
              <input
                className="whdisc-input"
                placeholder="Shopify customer ID"
                value={newCustomerId}
                onChange={e => setNewCustomerId(e.target.value)}
                disabled={saving}
              />
              <input
                className="whdisc-input"
                placeholder="Label (e.g. store name)"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                disabled={saving}
              />
              <div className="whdisc-pct-wrap">
                <input
                  className="whdisc-input whdisc-input-pct"
                  type="number" step="0.01" min="0" max="100"
                  placeholder="%"
                  value={newDiscount}
                  onChange={e => setNewDiscount(e.target.value)}
                  disabled={saving}
                />
                <span className="whdisc-pct-sign">%</span>
              </div>
              <button className="whdisc-btn-primary" type="submit"
                      disabled={saving || !newCustomerId.trim() || !newDiscount}>
                Save
              </button>
            </form>
            {whslCustomers.length > 0 && (
              <p className="whdisc-muted-sm">
                WHSL accounts: {whslCustomers.map(c => (
                  <span key={c.id} className="whdisc-tag-hint">
                    {c.display} <em>({c.id}{c.is_eu ? ', EU' : ''})</em>
                  </span>
                ))}
              </p>
            )}
          </div>

          {accounts.length === 0 ? (
            <p className="whdisc-muted">No custom account rates configured yet — everyone gets the default.</p>
          ) : (
            <div className="whdisc-table-wrap">
              <table className="whdisc-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Customer ID</th>
                    <th>Discount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(acct => (
                    <tr key={acct.customer_id}>
                      <td className="whdisc-account-label">
                        {acct.label || customerById[String(acct.customer_id)]?.display || '—'}
                      </td>
                      <td className="whdisc-account-id">{acct.customer_id}</td>
                      <td>
                        <div className="whdisc-pct-wrap whdisc-pct-wrap-sm">
                          <input
                            className="whdisc-input whdisc-input-pct-sm"
                            type="number" step="0.01" min="0" max="100"
                            defaultValue={acct.discount}
                            disabled={saving}
                            onBlur={e => {
                              if (e.target.value && e.target.value !== acct.discount) {
                                editAccountDiscount(acct, e.target.value);
                              }
                            }}
                          />
                          <span className="whdisc-pct-sign">%</span>
                        </div>
                      </td>
                      <td>
                        <button className="whdisc-remove" disabled={saving}
                                onClick={() => removeAccount(acct.customer_id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
